/**
 * useVisibleAttributeFields — список атрибутов, которые юзер видит в rail
 * и которые должны появляться в create-диалоге в горизонтальном ряду.
 *
 * Правила фильтрации (те же, что в `ProjectFieldsRail` / `TaskFieldsRail`):
 *   • `config.rail_section !== "hidden"`
 *   • `config.page_placement === "sidebar" || "both"`
 *   • `slug !== "name" && slug !== "description"` — у этих атрибутов
 *     placement="main", они не должны дублироваться в chip-row.
 *
 * Порядок берётся из `attribute_layout` (workspace + personal), fallback —
 * `display_order` из дефолтного списка.
 *
 * Использование:
 *   const { fields, isLoading } = useVisibleAttributeFields("task", "task_fields_rail")
 */
import { useMemo } from "react"
import {
  useAttributeDefinitions,
  useAttributeLayout,
  type AttributeDefinition,
  type AttributeLayoutItem,
} from "@/api/metadata/useAttributeDefinitions"

type RailSection = "details" | "additional" | "hidden"

function getRailSection(definition: AttributeDefinition): RailSection {
  const configured = definition.config.rail_section
  if (configured === "details") return "details"
  if (configured === "additional") return "additional"
  if (configured === "hidden") return "hidden"
  return "details"
}

function getPagePlacement(definition: AttributeDefinition): "main" | "sidebar" | "both" | "hidden" {
  const configured = definition.config.page_placement
  if (configured === "main" || configured === "sidebar" || configured === "both" || configured === "hidden") {
    return configured
  }
  if (definition.slug === "name" || definition.slug === "description") return "main"
  return "sidebar"
}

function shouldIncludeInCreateChipRow(definition: AttributeDefinition) {
  if (getRailSection(definition) === "hidden") return false
  const placement = getPagePlacement(definition)
  if (placement !== "sidebar" && placement !== "both") return false
  if (definition.slug === "name" || definition.slug === "description") return false
  if (definition.slug === "created_at" || definition.slug === "updated_at") return false
  if (definition.attribute_type === "long_text") return false
  if (definition.attribute_type === "json") return false
  return true
}

export function useVisibleAttributeFields(
  objectTypeSlug: string,
  surface: string,
): { fields: AttributeDefinition[]; isLoading: boolean } {
  const { data: definitions = [], isLoading: defsLoading } = useAttributeDefinitions(objectTypeSlug)
  const { data: layoutState } = useAttributeLayout(objectTypeSlug, surface)

  const fields = useMemo(() => {
    const visible = definitions.filter(shouldIncludeInCreateChipRow)
    if (visible.length === 0) return visible

    const orderByID = new Map<string, number>()
    for (const definition of visible) {
      orderByID.set(definition.id, definition.display_order)
    }

    const layers = [layoutState?.workspace?.items ?? [], layoutState?.personal?.items ?? []]
    for (const items of layers) {
      for (const item of items) {
        if (orderByID.has(item.attribute_id)) {
          orderByID.set(item.attribute_id, item.order)
        }
      }
    }

    return [...visible].sort((left, right) => {
      const lo = orderByID.get(left.id) ?? left.display_order
      const ro = orderByID.get(right.id) ?? right.display_order
      return lo - ro
    })
  }, [definitions, layoutState])

  return { fields, isLoading: defsLoading }
}

export type { AttributeDefinition, AttributeLayoutItem }
